import { LogOut, ShieldCheck, UserCircle2 } from "lucide-react";
import { Section, Field } from "../applications/shared/InfoBlock";
import { useSession } from "@/lib/auth/session";
import { confirmAction } from "@/app/providers/ConfirmDialog";

export function MyProfileScreen() {
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);

  if (!user) {
    return (
      <div className="nx-card p-8 text-center text-fg-subtle text-sm">
        No hay sesión activa.
      </div>
    );
  }

  const isOwner = user.permissions.includes("*");

  const handleLogout = async () => {
    const ok = await confirmAction({
      title: "Cerrar sesión",
      message: "Vas a tener que volver a loguearte con Google.",
      confirmLabel: "Cerrar sesión",
      tone: "danger",
    });
    if (ok === true) {
      logout();
      window.location.href = "/login";
    }
  };

  return (
    <div className="flex flex-col gap-4 animate-fade-in max-w-3xl">
      <header>
        <p className="nx-eyebrow mb-1">Nexus Control · Mi perfil</p>
        <h1 className="nx-h1">Mi perfil</h1>
        <p className="text-sm text-fg-muted mt-1">
          Vista de tu identidad y permisos efectivos en Nexus. Para editar
          datos personales (avatar, nombre), usá tu perfil en{" "}
          <a
            href="https://mixcore.club/usuario"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            mixcore.club/usuario
          </a>
          .
        </p>
      </header>

      <section className="nx-card p-5">
        <div className="flex items-center gap-4 mb-4">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              className="w-16 h-16 rounded-full object-cover border border-border"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-bg-subtle border border-border flex items-center justify-center">
              <UserCircle2 size={28} className="text-fg-subtle" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-fg">{user.name}</h2>
            <a
              className="text-sm text-accent hover:underline break-all"
              href={`mailto:${user.email}`}
            >
              {user.email}
            </a>
          </div>
          {isOwner && (
            <span className="nx-badge-accent">
              <ShieldCheck size={10} className="inline mr-1" />
              Owner
            </span>
          )}
        </div>

        <Section title="Datos básicos">
          <Field label="User ID" value={user.id} />
          <Field
            label="Roles asignados"
            value={user.roles.length === 0 ? "Ninguno" : `${user.roles.length}`}
          />
          <Field
            label="Permisos efectivos"
            value={isOwner ? "Todos (★ wildcard)" : `${user.permissions.length}`}
          />
        </Section>

        <Section title={`Roles (${user.roles.length})`}>
          <div className="col-span-2 flex flex-wrap gap-2">
            {user.roles.length === 0 ? (
              <span className="text-fg-subtle text-xs">Sin roles asignados.</span>
            ) : (
              user.roles.map((r) => (
                <span key={r.slug} className="nx-badge-info">
                  {r.name}
                  <code className="ml-1 opacity-70 text-2xs">({r.slug})</code>
                </span>
              ))
            )}
          </div>
        </Section>

        <Section title={`Permisos efectivos (${isOwner ? "wildcard" : user.permissions.length})`}>
          <div className="col-span-2">
            {isOwner ? (
              <div className="flex items-start gap-2 text-xs p-3 rounded-md border border-accent/40 bg-accent-soft text-fg">
                <ShieldCheck size={14} className="text-accent shrink-0 mt-0.5" />
                <span>
                  Tu rol Owner incluye el wildcard <code>*</code>. Tenés acceso
                  a todos los permisos existentes y futuros del sistema.
                </span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {user.permissions
                  .slice()
                  .sort()
                  .map((p) => (
                    <code
                      key={p}
                      className="text-2xs text-fg bg-bg-subtle px-1.5 py-0.5 rounded font-mono"
                    >
                      {p}
                    </code>
                  ))}
              </div>
            )}
          </div>
        </Section>
      </section>

      <section className="nx-card p-4">
        <h3 className="text-sm font-semibold text-fg mb-3">Sesión</h3>
        <div className="flex items-center justify-between">
          <p className="text-2xs text-fg-muted">
            Cierra la sesión de Nexus. No afecta tu sesión en mixcore.club.
          </p>
          <button onClick={handleLogout} className="nx-btn-danger text-xs">
            <LogOut size={12} /> Cerrar sesión
          </button>
        </div>
      </section>
    </div>
  );
}
